import _ from "lodash";
import {
  NoAssignmentIllustration,
  NoMessagesIllustration,
  NothingAssignedIllustration,
  NothingScheduledIllustration,
} from "@toddle-edu/ds-theme";
import { ACTIVE, CLASS_FLOW, CLASS_STREAM } from "Constants/stringConstants";
import ACLStore from "lib/aclStore";

import { ASSIGNMENT_AND_PROJECT_STATUS } from "./AssignmentAndDeadlineModules";
import { ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION } from "Constants/permissionConstants";
import moment from "moment";
import { getDateTimeStringsBasedOnFormat } from "Utils";
import { ASSIGNMENT_FEED_DATE_TIME_FORMAT } from "ClassRoom/modules/Constants";

// this logic only work for to_review tab
// the purpose of this function to show the label of the count of evaluation/share pending in to review tab
export const getEvaluationCountLabel = ({
  isPrivate,
  isTeacherEvaluationEnabled,
  summaryCounts,
  isStudentSubmissionEnabled,
  t,
}) => {
  const { evaluationPending, evaluationSharedPending } = summaryCounts || {};
  if (
    _.every([evaluationPending, evaluationSharedPending], value => value === 0)
  )
    return null;

  if (isPrivate) {
    // private
    if (!evaluationPending) return null;

    if (isTeacherEvaluationEnabled && isStudentSubmissionEnabled) {
      return t("classRoom:evaluation_pending_with_count", {
        count: evaluationPending,
      });
    }

    if (!isTeacherEvaluationEnabled && isStudentSubmissionEnabled) {
      return t("classRoom:submission_need_reviewed_with_count", {
        count: evaluationPending,
      });
    }

    if (isTeacherEvaluationEnabled && !isStudentSubmissionEnabled) {
      return t("classRoom:evaluation_pending_with_count", {
        count: evaluationPending,
      });
    }

    return null;
  } else {
    // public post
    if (isTeacherEvaluationEnabled && isStudentSubmissionEnabled) {
      if (evaluationPending) {
        return t("classRoom:evaluation_pending_with_count", {
          count: evaluationPending,
        });
      }
      if (evaluationSharedPending) {
        return t("classRoom:evaluation_shared_pending_with_count", {
          count: evaluationSharedPending,
        });
      }
      return null;
    }

    if (!isTeacherEvaluationEnabled && isStudentSubmissionEnabled) {
      if (!evaluationPending) return null;
      return t("classRoom:submission_need_reviewed_with_count", {
        count: evaluationPending,
      });
    }

    if (isTeacherEvaluationEnabled && !isStudentSubmissionEnabled) {
      if (evaluationPending) {
        return t("classRoom:evaluation_pending_with_count", {
          count: evaluationPending,
        });
      }
      if (evaluationSharedPending) {
        return t("classRoom:evaluation_shared_pending_with_count", {
          count: evaluationSharedPending,
        });
      }
      return null;
    }

    return null;
  }
};

export const getEmptyStateObj = (activeTab, t) => {
  const assignmentAndProjectStatusDataMapping = {
    [ASSIGNMENT_AND_PROJECT_STATUS.ALL_ITEMS]: {
      illustration: NoAssignmentIllustration,
      title: t("classRoom:all_assignments_empty_state_title"),
      subtitle: t("classRoom:all_published_assignment_appear_here"),
    },
    [ASSIGNMENT_AND_PROJECT_STATUS.TO_REVIEW]: {
      illustration: NothingAssignedIllustration,
      title: t("common:nothing_review_yet"),
      subtitle: t("classRoom:assignment_submission_appear_here"),
    },
    [ASSIGNMENT_AND_PROJECT_STATUS.UNREAD]: {
      illustration: NoMessagesIllustration,
      title: t("classRoom:no_assignment_with_unread_message"),
      subtitle: t("classRoom:assignment_unread_message_appear_here"),
    },
    [ASSIGNMENT_AND_PROJECT_STATUS.SCHEDULED]: {
      illustration: NothingScheduledIllustration,
      title: t("classRoom:nothing_scheduled"),
      subtitle: t("classRoom:scheduled_assignment_appere_here"),
    },
  };

  return _.get(assignmentAndProjectStatusDataMapping, activeTab, {});
};

export const getADMQueryVariables = ({
  userId,
  userEntityType,
  selectedTab,
  orderBy = "PUBLISHED_AT",
  orderByDirection = "DESC",
  first = 25,
  filters,
  INCLUDE_ITEM_TYPES_FOR_UNREAD_MESSAGES_COUNT,
}) => {
  const {
    contentTypes,
    searchText,
    createdBy,
    folderTopic,
    assessmentTypes,
    excludedAssessmentTypes,
    courseIds,
    allCourseIds,
    academicTermIds,
  } = filters;

  const sendFilters = {
    excludedAssessmentTypes,
    status: selectedTab,
    entityTypes: ["ASSIGNMENT"],
  };

  if (searchText != "") {
    sendFilters["searchText"] = searchText;
  }
  if (contentTypes != "ALL") {
    sendFilters["contentTypes"] = [contentTypes];
  }
  if (assessmentTypes != "ALL") {
    sendFilters["assessmentTypes"] = [assessmentTypes];
  }
  if (createdBy != "ALL") {
    if (_.isArray(createdBy)) {
      sendFilters["createdByIds"] = createdBy;
    } else {
      sendFilters["createdByIds"] = [createdBy];
    }
  }

  if (!_.isEmpty(academicTermIds)) {
    sendFilters["academicTermIds"] = academicTermIds;
  } else {
    sendFilters["academicTermIds"] = [];
  }

  if (folderTopic !== "ALL") {
    sendFilters["folderIds"] = folderTopic;
  }

  if (!_.isEmpty(courseIds)) {
    sendFilters["courseIds"] = _.sortBy(courseIds);
  } else if (!_.isEmpty(allCourseIds)) {
    // case when no courses are selected in class stream manager
    // send all courseIds of user
    sendFilters["courseIds"] = _.sortBy(allCourseIds);
  }

  return {
    userId,
    type: userEntityType,
    filters: sendFilters,
    first,
    orderByDirection,
    orderBy,
    includeMessagesSummary:
      selectedTab === ASSIGNMENT_AND_PROJECT_STATUS.UNREAD,
    includeAssignedStudents:
      selectedTab === ASSIGNMENT_AND_PROJECT_STATUS.UNREAD,
    unreadMessageCountFilters: {
      includeItemTypesV2: INCLUDE_ITEM_TYPES_FOR_UNREAD_MESSAGES_COUNT,
    },
  };
};

export const getADMStatusWiseCountQueryVariables = ({
  userId,
  userEntityType,
  academicYearId,
  filters,
}) => {
  const {
    excludedAssessmentTypes,
    courseIds,
    allCourseIds,
    academicTermIds,
    createdBy,
  } = filters;

  const sendFilters = {
    excludedAssessmentTypes,
    academicYearId,
    entityTypes: ["ASSIGNMENT"],
  };

  if (createdBy != "ALL") {
    if (_.isArray(createdBy)) {
      sendFilters["createdByIds"] = createdBy;
    } else {
      sendFilters["createdByIds"] = [createdBy];
    }
  }

  if (!_.isEmpty(academicTermIds)) {
    sendFilters["academicTermIds"] = academicTermIds;
  } else {
    sendFilters["academicTermIds"] = [];
  }

  if (!_.isEmpty(courseIds)) {
    sendFilters["courseIds"] = _.sortBy(courseIds);
  } else if (!_.isEmpty(allCourseIds)) {
    // case when no courses are selected in class stream manager
    // send all courseIds of user
    sendFilters["courseIds"] = _.sortBy(allCourseIds);
  }

  return {
    userId,
    type: userEntityType,
    filters: sendFilters,
  };
};

export const getADMCoursesQueryVariables = ({
  userId,
  currentCurriculumProgramId,
  userType,
  academicYearId,
  getAssignmentConsumingParentModulesPermission,
}) => {
  return {
    id: userId,
    filters: {
      curriculumProgramIds: [currentCurriculumProgramId],
      archivalState: ACTIVE,
      fetchOnlyMappedCourses: false,
      academicYear: academicYearId,
    },
    curriculumProgramIds: currentCurriculumProgramId
      ? [currentCurriculumProgramId]
      : [],
    enabledModulesFilters: {
      moduleNames: [CLASS_STREAM, CLASS_FLOW],
      userType,
    },
    aclControlParameterFilter: ACLStore.can(
      ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION
    )
      ? {
          controlParameters: [
            ...getAssignmentConsumingParentModulesPermission("VIEW"),
            ...getAssignmentConsumingParentModulesPermission("MANAGE"),
          ],
        }
      : undefined,
  };
};

export const getDateToShow = ({ dueOn, closesOn, t }) => {
  const hasDueDate = !_.isEmpty(dueOn);
  const hasCloseDate = !_.isEmpty(closesOn);

  // If no dates exist, don't show any date
  if (!hasDueDate && !hasCloseDate) {
    return null;
  }

  let dateToFormat = null;
  let prefix = null;

  // Determine which date and prefix to use
  if (!hasCloseDate) {
    dateToFormat = dueOn;
    prefix = t("classRoom:due_capital");
  } else if (!hasDueDate) {
    dateToFormat = closesOn;
    prefix = t("classRoom:closes_capital");
  } else {
    // Both dates exist - show close date if due date has passed
    const isDueDatePassed = moment(dueOn).isBefore(moment());

    if (isDueDatePassed) {
      dateToFormat = closesOn;
      prefix = t("classRoom:closes_capital");
    } else {
      dateToFormat = dueOn;
      prefix = t("classRoom:due_capital");
    }
  }

  // Format and return the date with prefix
  const formattedDate = getDateTimeStringsBasedOnFormat({
    inputDateTimeString: dateToFormat,
    dateTimeFormat: ASSIGNMENT_FEED_DATE_TIME_FORMAT,
  }).dateTimeString;

  return `${prefix}: ${formattedDate}`;
};
